/**
 * Shared utilities for the payment redirect download landing (V2, V6 A/B test).
 * - Download landing version: V2 vs V6, split 50/50 when no valid `wldpVersion` param.
 * - Bonus entries parsing: used by RedirectDownload (V2).
 *
 * Display contract (bonus entries):
 * - showFree === true  → no valid entriesPerMonth in URL: show generic "FREE BONUS ENTRIES" copy.
 * - showFree === false → valid entriesPerMonth (integer >= 1): show "{bonusEntriesNumber} BONUS ENTRIES".
 * Invalid, missing, zero, or negative all yield showFree === true.
 */

/** Supported download landing versions. Single source of truth — add new versions here. */
export const DOWNLOAD_LANDING_VERSIONS = ["2", "6"] as const;

export type DownloadLandingVersion = (typeof DOWNLOAD_LANDING_VERSIONS)[number];

const DOWNLOAD_LANDING_VERSIONS_SET = new Set<string>(DOWNLOAD_LANDING_VERSIONS);

/**
 * Returns the download landing version from the URL param, or a random 50/50 choice
 * when missing/invalid (e.g. legacy `wldpVersion=5`).
 * When the param is missing, call this inside useMemo/useState (once per mount) to avoid
 * re-rolling the variant on re-renders.
 */
export const getDownloadLandingVersion = (
  urlParam: string | null | undefined,
): DownloadLandingVersion => {
  const trimmed = urlParam?.trim();
  if (trimmed && DOWNLOAD_LANDING_VERSIONS_SET.has(trimmed)) {
    return trimmed as DownloadLandingVersion;
  }
  const randomIndex = Math.floor(
    Math.random() * DOWNLOAD_LANDING_VERSIONS.length,
  );
  return DOWNLOAD_LANDING_VERSIONS[randomIndex];
};

export interface BonusEntriesFromUrl {
  /** Valid entries-per-month from URL (integer >= 1), or null when missing/invalid. */
  bonusEntriesNumber: number | null;
  /** True when bonusEntriesNumber is null → use "FREE" messaging; false → use number in copy. */
  showFree: boolean;
}

export const getBonusEntriesFromSearchParams = (
  search: string,
): BonusEntriesFromUrl => {
  const entriesPerMonthParam = new URLSearchParams(search).get(
    "entriesPerMonth",
  );
  const parsed =
    entriesPerMonthParam != null && entriesPerMonthParam !== ""
      ? parseInt(entriesPerMonthParam, 10)
      : NaN;
  const bonusEntriesNumber =
    Number.isFinite(parsed) && parsed >= 1 ? parsed : null;
  const showFree = bonusEntriesNumber == null;
  return { bonusEntriesNumber, showFree };
};
